/**
 * Binary arithmetic with the working kept, and fixed-width bitwise operations.
 *
 * Arithmetic is exact at any length (BigInt) and returns the column-by-column working a
 * textbook shows: carries for addition, borrows for subtraction, shifted partial products
 * for multiplication and the bring-down steps of long division. The working is built on
 * the operands' magnitudes; the sign is applied by the usual rules and reported as a note.
 *
 * Bitwise operations are defined at a width, because NOT, shifts and rotations mean
 * nothing without one.
 */

import { absBig, BIG_ONE, BIG_ZERO } from "@/lib/tool-kit/math/bigint";
import { bitLength, maskOf, patternBits, toPattern, toSigned } from "./bits";

// biome-ignore-start lint/suspicious/noBitwiseOperators: binary arithmetic and bitwise operations are this module's whole job

/** Operands longer than this still get an exact answer, but no column working. */
export const WORKING_LIMIT = 64;

export type ArithOp = "add" | "subtract" | "multiply" | "divide";

export interface AddWorking {
  kind: "add";
  /** Every row has the same length, most significant bit first. */
  a: string;
  b: string;
  /** The carry arriving into each column. */
  carries: string;
  result: string;
  /** How many leading columns are all zero (drawn faint). */
  leading: number;
}

export interface SubtractWorking {
  kind: "subtract";
  /** The larger magnitude. */
  a: string;
  /** The smaller magnitude, subtracted from `a`. */
  b: string;
  /** The borrow arriving into each column from the column to its right. */
  borrows: string;
  result: string;
  leading: number;
}

export interface PartialProduct {
  /** Position of the multiplier bit (0 is the least significant). */
  shift: number;
  /** The multiplicand shifted left by `shift`. */
  bits: string;
  /** The running total after adding this partial product. */
  running: string;
}

export interface MultiplyWorking {
  kind: "multiply";
  multiplicand: string;
  multiplier: string;
  partials: PartialProduct[];
  product: string;
}

export interface DivideStep {
  /** Position of the dividend bit brought down. */
  position: number;
  bit: string;
  /** The running remainder after the bit is brought down. */
  partial: string;
  /** Whether the divisor went in (and was subtracted). */
  subtracted: boolean;
  /** The remainder carried to the next step. */
  remainder: string;
  quotientBit: string;
}

export interface DivideWorking {
  kind: "divide";
  dividend: string;
  divisor: string;
  steps: DivideStep[];
  quotient: string;
  remainder: string;
}

export type Working =
  | AddWorking
  | SubtractWorking
  | MultiplyWorking
  | DivideWorking;

export type ArithNote = "mixedSigns" | "signApplied" | "truncated";

export type ArithOutcome =
  | {
      ok: true;
      op: ArithOp;
      value: bigint;
      /** Division only. */
      remainder: bigint | null;
      working: Working | null;
      notes: ArithNote[];
    }
  | { ok: false; error: "divideByZero" };

function leadingZeroColumns(rows: string[]): number {
  const width = rows[0]?.length ?? 0;
  let count = 0;
  while (count < width - 1 && rows.every((row) => row[count] === "0")) {
    count += 1;
  }
  return count;
}

function addColumns(x: bigint, y: bigint): AddWorking {
  const width = Math.max(bitLength(x), bitLength(y)) + 1;
  const a = x.toString(2).padStart(width, "0");
  const b = y.toString(2).padStart(width, "0");
  const carries: string[] = new Array(width).fill("0");
  const sum: string[] = new Array(width).fill("0");
  let carry = 0;
  for (let i = width - 1; i >= 0; i--) {
    carries[i] = String(carry);
    const total = Number(a[i]) + Number(b[i]) + carry;
    sum[i] = String(total % 2);
    carry = total >= 2 ? 1 : 0;
  }
  const result = sum.join("");
  return {
    kind: "add",
    a,
    b,
    carries: carries.join(""),
    result,
    leading: leadingZeroColumns([a, b, result]),
  };
}

/** `x − y` for x ≥ y, column by column. */
function subtractColumns(x: bigint, y: bigint): SubtractWorking {
  const width = Math.max(bitLength(x), 1);
  const a = x.toString(2).padStart(width, "0");
  const b = y.toString(2).padStart(width, "0");
  const borrows: string[] = new Array(width).fill("0");
  const diff: string[] = new Array(width).fill("0");
  let borrow = 0;
  for (let i = width - 1; i >= 0; i--) {
    borrows[i] = String(borrow);
    let digit = Number(a[i]) - Number(b[i]) - borrow;
    if (digit < 0) {
      digit += 2;
      borrow = 1;
    } else {
      borrow = 0;
    }
    diff[i] = String(digit);
  }
  const result = diff.join("");
  return {
    kind: "subtract",
    a,
    b,
    borrows: borrows.join(""),
    result,
    leading: leadingZeroColumns([a, b, result]),
  };
}

function multiplyWorking(x: bigint, y: bigint): MultiplyWorking {
  const multiplier = y.toString(2);
  const partials: PartialProduct[] = [];
  let running = BIG_ZERO;
  for (let shift = 0; shift < multiplier.length; shift++) {
    if (multiplier.at(-1 - shift) !== "1") {
      continue;
    }
    const shifted = x << BigInt(shift);
    running += shifted;
    partials.push({
      shift,
      bits: shifted.toString(2),
      running: running.toString(2),
    });
  }
  return {
    kind: "multiply",
    multiplicand: x.toString(2),
    multiplier,
    partials,
    product: (x * y).toString(2),
  };
}

function divideWorking(x: bigint, y: bigint): DivideWorking {
  const dividend = x.toString(2);
  const steps: DivideStep[] = [];
  let remainder = BIG_ZERO;
  let quotient = "";
  for (let i = 0; i < dividend.length; i++) {
    const bit = dividend[i];
    remainder = (remainder << BIG_ONE) | BigInt(bit);
    const partial = remainder.toString(2);
    const subtracted = remainder >= y;
    if (subtracted) {
      remainder -= y;
    }
    const quotientBit = subtracted ? "1" : "0";
    quotient += quotientBit;
    steps.push({
      position: dividend.length - 1 - i,
      bit,
      partial,
      subtracted,
      remainder: remainder.toString(2),
      quotientBit,
    });
  }
  return {
    kind: "divide",
    dividend,
    divisor: y.toString(2),
    steps,
    quotient: BigInt(`0b${quotient}`).toString(2),
    remainder: remainder.toString(2),
  };
}

function withinLimit(...values: bigint[]): boolean {
  return values.every((v) => bitLength(absBig(v)) <= WORKING_LIMIT);
}

function addOrSubtract(x: bigint, y: bigint): ArithOutcome {
  const sameSign = x >= BIG_ZERO === y >= BIG_ZERO;
  const ax = absBig(x);
  const ay = absBig(y);
  const value = x + y;
  let working: Working | null = null;
  if (withinLimit(x, y)) {
    if (sameSign) {
      working = addColumns(ax, ay);
    } else {
      working = ax >= ay ? subtractColumns(ax, ay) : subtractColumns(ay, ax);
    }
  }
  return {
    ok: true,
    op: "add",
    value,
    remainder: null,
    working,
    notes: sameSign ? [] : ["mixedSigns"],
  };
}

/** Run one arithmetic operation on signed integers. */
export function calculate(op: ArithOp, a: bigint, b: bigint): ArithOutcome {
  if (op === "add") {
    return addOrSubtract(a, b);
  }
  if (op === "subtract") {
    const outcome = addOrSubtract(a, -b);
    return outcome.ok ? { ...outcome, op: "subtract" } : outcome;
  }
  const negative = a < BIG_ZERO !== b < BIG_ZERO;
  const ax = absBig(a);
  const ay = absBig(b);
  if (op === "multiply") {
    return {
      ok: true,
      op,
      value: a * b,
      remainder: null,
      working: withinLimit(a, b) ? multiplyWorking(ax, ay) : null,
      notes: negative && a * b !== BIG_ZERO ? ["signApplied"] : [],
    };
  }
  if (b === BIG_ZERO) {
    return { ok: false, error: "divideByZero" };
  }
  const notes: ArithNote[] = [];
  if (negative && a !== BIG_ZERO) {
    notes.push("signApplied");
  }
  if (a < BIG_ZERO || b < BIG_ZERO) {
    notes.push("truncated");
  }
  return {
    ok: true,
    op,
    value: a / b,
    remainder: a % b,
    working: withinLimit(a) ? divideWorking(ax, ay) : null,
    notes,
  };
}

// ---------------------------------------------------------------------------
// Bitwise
// ---------------------------------------------------------------------------

export type BitwiseOp =
  | "and"
  | "or"
  | "xor"
  | "nand"
  | "nor"
  | "xnor"
  | "not"
  | "shl"
  | "shr"
  | "sar"
  | "rol"
  | "ror";

export const WIDTHS = [4, 8, 16, 32, 64, 128] as const;

/** Operations that take a shift or rotate count instead of a second bit pattern. */
export function isShiftOp(op: BitwiseOp): boolean {
  return (
    op === "shl" || op === "shr" || op === "sar" || op === "rol" || op === "ror"
  );
}

export type BitwiseOutcome =
  | {
      ok: true;
      width: number;
      op: BitwiseOp;
      a: bigint;
      /** Second pattern for two-operand ops. */
      b: bigint | null;
      /** The count for shifts and rotations. */
      count: number | null;
      result: bigint;
      /** Bits that fell off the end in a shift (as a bit string), if any. */
      shiftedOut: string | null;
      signed: bigint;
    }
  | { ok: false; error: "operandTooWide" | "countTooLarge" };

/** Largest shift or rotate count accepted. */
export const MAX_COUNT = 4096;

function logic(op: BitwiseOp, a: bigint, b: bigint, width: number): bigint {
  const mask = maskOf(width);
  switch (op) {
    case "and":
      return a & b;
    case "or":
      return a | b;
    case "xor":
      return a ^ b;
    case "nand":
      return ~(a & b) & mask;
    case "nor":
      return ~(a | b) & mask;
    case "xnor":
      return ~(a ^ b) & mask;
    default:
      return ~a & mask;
  }
}

function shifted(
  op: BitwiseOp,
  a: bigint,
  count: number,
  width: number
): { result: bigint; out: string } {
  const bits = patternBits(a, width);
  const over = Math.min(count, width);
  switch (op) {
    case "shl":
      return {
        result: toPattern(a << BigInt(count), width),
        out: bits.slice(0, over),
      };
    case "shr":
      return {
        result: a >> BigInt(count),
        out: bits.slice(width - over),
      };
    case "sar":
      return {
        result: toPattern(toSigned(a, width) >> BigInt(count), width),
        out: bits.slice(width - over),
      };
    case "rol": {
      const n = count % width;
      return {
        result: toPattern((a << BigInt(n)) | (a >> BigInt(width - n)), width),
        out: "",
      };
    }
    default: {
      const n = count % width;
      return {
        result: toPattern((a >> BigInt(n)) | (a << BigInt(width - n)), width),
        out: "",
      };
    }
  }
}

/**
 * Apply a bitwise operation at `width` bits. `a` and `b` are unsigned bit patterns;
 * `count` is the shift or rotate distance for those operations.
 */
export function bitwise(
  op: BitwiseOp,
  a: bigint,
  b: bigint,
  count: number,
  width: number
): BitwiseOutcome {
  if (
    bitLength(a) > width ||
    (!isShiftOp(op) && op !== "not" && bitLength(b) > width)
  ) {
    return { ok: false, error: "operandTooWide" };
  }
  if (isShiftOp(op)) {
    if (count > MAX_COUNT) {
      return { ok: false, error: "countTooLarge" };
    }
    const { result, out } = shifted(op, a, count, width);
    return {
      ok: true,
      width,
      op,
      a,
      b: null,
      count,
      result,
      shiftedOut: out.length > 0 ? out : null,
      signed: toSigned(result, width),
    };
  }
  const result = logic(op, a, b, width);
  return {
    ok: true,
    width,
    op,
    a,
    b: op === "not" ? null : b,
    count: null,
    result,
    shiftedOut: null,
    signed: toSigned(result, width),
  };
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: end of the binary calculator
